import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import GroupManager from '@/components/attribute-management/GroupManager';
import AttributeManager from '@/components/attribute-management/AttributeManager';
import { validateName } from '@/lib/utils';

export interface AttributeGroup {
  id: string;
  name: string;
  createdAt: Date;
}

export interface Attribute {
  id: string;
  name: string;
  displayName: string;
  type: 'Integer' | 'Number' | 'Text' | 'Float' | 'Boolean' | 'String Array';
  uiElementType: 'Checkbox' | 'Dropdown (Single)' | 'Dropdown (Multi)' | 'Textbox' | 'Textarea';
  groupId: string;
  order: number;
  values?: string[];
}

const AttributeManagement = () => {
  const [groups, setGroups] = useState<AttributeGroup[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [activeGroupId, setActiveGroupId] = useState<string>('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [error, setError] = useState("");
  // Load data from localStorage on mount
  useEffect(() => {
    const savedGroups = localStorage.getItem('attribute-groups');
    const savedAttributes = localStorage.getItem('attribute-attributes');
    const savedActiveGroupId = localStorage.getItem('attribute-active-group');

    if (savedGroups) {
      const parsedGroups = JSON.parse(savedGroups).map((g: any) => ({
        ...g,
        createdAt: new Date(g.createdAt)
      }));
      setGroups(parsedGroups);
      
      if (savedActiveGroupId && parsedGroups.find((g: AttributeGroup) => g.id === savedActiveGroupId)) {
        setActiveGroupId(savedActiveGroupId);
      } else {
        setActiveGroupId(parsedGroups[0]?.id || '');
      }
    } else {
      // Initialize with default data
      const defaultGroups = [
        { id: '1', name: 'Basic Info', createdAt: new Date() },
        { id: '2', name: 'Pricing', createdAt: new Date() },
      ];
      setGroups(defaultGroups);
      setActiveGroupId(defaultGroups[0].id);
    }

    if (savedAttributes) {
      setAttributes(JSON.parse(savedAttributes));
    } else {
      // Initialize with default data
      const defaultAttributes = [
        {
          id: '1',
          name: 'product_name',
          displayName: 'Product Name',
          type: 'Text' as const,
          uiElementType: 'Textbox' as const,
          groupId: '1',
          order: 1
        },
        {
          id: '2',
          name: 'description',
          displayName: 'Description',
          type: 'Text' as const,
          uiElementType: 'Textarea' as const,
          groupId: '1',
          order: 2
        },
        {
          id: '3',
          name: 'price',
          displayName: 'Price',
          type: 'Float' as const,
          uiElementType: 'Textbox' as const,
          groupId: '2',
          order: 1
        }
      ];
      setAttributes(defaultAttributes);
    }
  }, []);

  const handleGroupsChange = (newGroups: AttributeGroup[]) => {
    setGroups(newGroups);
    localStorage.setItem('attribute-groups', JSON.stringify(newGroups));
    // If active group was deleted, switch to first available group
    if (!newGroups.find(g => g.id === activeGroupId)) {
      const newActiveGroupId = newGroups[0]?.id || '';
      setActiveGroupId(newActiveGroupId);
      localStorage.setItem('attribute-active-group', newActiveGroupId);
    }
  };

  const handleAttributesChange = (newAttributes: Attribute[]) => {
    setAttributes(newAttributes);
    localStorage.setItem('attribute-attributes', JSON.stringify(newAttributes));
  };

  const handleActiveGroupChange = (groupId: string) => {
    setActiveGroupId(groupId);
    localStorage.setItem('attribute-active-group', groupId);
  };

  const handleImportExcel = async (file: File) => {
    try {
      // Dynamic import of xlsx
      const XLSX = await import('xlsx');
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            toast.error('Excel file must contain headers and data');
            return;
          }

          const headers = (jsonData[0] as string[]).map(h => h?.toString().trim() || '');
          
          if (!headers.includes('Group') || !headers.includes('Name') || !headers.includes('Display Name')) {
            toast.error('Excel must contain Group, Name, and Display Name columns');
            return;
          }

          const newGroups: AttributeGroup[] = [];
          const newAttributes: Attribute[] = [];
          
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i] as string[];
            const groupName = row[headers.indexOf('Group')]?.toString().trim();
            const name = row[headers.indexOf('Name')]?.toString().trim();
            const displayName = row[headers.indexOf('Display Name')]?.toString().trim();
            const type = row[headers.indexOf('Type')]?.toString().trim() || 'Text';
            const uiElementType = row[headers.indexOf('UI Element Type')]?.toString().trim() || 'Textbox';
            const valuesString = row[headers.indexOf('Values')]?.toString().trim() || '';
            const attributeValues = valuesString ? valuesString.split(',').map(v => v.trim()).filter(v => v) : [];

            if (!groupName || !name || !displayName) continue;

            // Find or create group (avoid duplicates)
            let group = [...groups, ...newGroups].find(g => g.name.toLowerCase() === groupName.toLowerCase());
            if (!group) {
              group = {
                id: Date.now().toString() + Math.random(),
                name: groupName,
                createdAt: new Date()
              };
              newGroups.push(group);
            }

            // Check for duplicate attribute in the same group
            const existingAttribute = [...attributes, ...newAttributes].find(attr => 
              attr.groupId === group!.id && attr.name.toLowerCase() === name.toLowerCase()
            );
            
            if (!existingAttribute) {
              const attribute: Attribute = {
                id: Date.now().toString() + Math.random(),
                name,
                displayName,
                type: type as Attribute['type'],
                uiElementType: uiElementType as Attribute['uiElementType'],
                groupId: group.id,
                order: newAttributes.filter(a => a.groupId === group.id).length + 1,
                values: attributeValues.length > 0 ? attributeValues : undefined
              };
              newAttributes.push(attribute);
            }
          }

          if (newGroups.length > 0 || newAttributes.length > 0) {
            const updatedGroups = [...groups, ...newGroups];
            const updatedAttributes = [...attributes, ...newAttributes];
            
            handleGroupsChange(updatedGroups);
            handleAttributesChange(updatedAttributes);
            
            toast.success(`Imported ${newGroups.length} groups and ${newAttributes.length} attributes`);
            setShowImportDialog(false);
          } else {
            toast.info('No new groups or attributes to import');
          }
        } catch (error) {
          toast.error('Error parsing Excel file');
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      toast.error('Error loading Excel parser');
    }
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    if (groups.some(g => g.name.toLowerCase() === newGroupName.toLowerCase())) {
      toast.error('Group name already exists');
      return;
    }

    const newGroup: AttributeGroup = {
      id: Date.now().toString(),
      name: newGroupName.trim(),
      createdAt: new Date()
    };

    const updatedGroups = [...groups, newGroup];
    handleGroupsChange(updatedGroups);
    setNewGroupName('');
    setShowCreateGroupDialog(false);
    handleActiveGroupChange(newGroup.id);
    
    toast.success(`Group "${newGroup.name}" created successfully`);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Attributes Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage attribute groups and their associated attributes
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowImportDialog(true)} className="gap-2">
              Import Attributes
            </Button>
            <Button variant="secondary" onClick={() => setShowCreateGroupDialog(true)} className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
              <Plus size={16} />
              Create New Group
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <GroupManager 
          groups={groups} 
          onGroupsChange={handleGroupsChange}
          activeGroupId={activeGroupId}
          onActiveGroupChange={handleActiveGroupChange}
          onAttributeDropOnGroup={handleAttributesChange}
          attributes={attributes}
        />
        
        <AttributeManager
          groups={groups}
          attributes={attributes}
          onAttributesChange={handleAttributesChange}
          activeGroupId={activeGroupId}
        />
      </div>

      {/* Import Excel Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Attributes from Excel</DialogTitle>
            <DialogDescription>
              Upload an Excel file with columns: Group, Name, Display Name, Type, UI Element Type, Values (comma-separated)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                      toast.error('Please upload an Excel file (.xlsx or .xls)');
                      return;
                    }
                    handleImportExcel(file);
                  }
                }}
                className="hidden"
                id="excel-upload"
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                <div className="space-y-2">
                  <div className="text-2xl">📊</div>
                  <p className="text-sm text-muted-foreground">
                    Click to select Excel file or drag and drop
                  </p>
                </div>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={showCreateGroupDialog} onOpenChange={() => {
        setShowCreateGroupDialog(false);
        setNewGroupName('');
        setError("");
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Group</DialogTitle>
            <DialogDescription>
              Enter a name for the new attribute group.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Input
                placeholder="Enter group name..."
                value={newGroupName}
                onChange={(e) => { setNewGroupName(e.target.value); setError(validateName(e.target.value)); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !error && newGroupName) handleCreateGroup(); }}
                autoFocus
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateGroupDialog(false);
              setNewGroupName('');
              setError("");
            }}>
              Cancel
            </Button>
            <Button disabled={!newGroupName || !!error} onClick={handleCreateGroup}>
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttributeManagement;